import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, X, Send, Sparkles, RotateCcw, Zap, AlertTriangle, Wrench, BarChart2, Target, MessageSquare, Loader2 } from 'lucide-react';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';

// ── Suggested quick questions ─────────────────────────────────────────────────
const SUGGESTED_QUESTIONS = [
  { label: 'Top root causes', icon: Target, q: 'What are the top root causes of breakdowns?' },
  { label: 'Worst equipment', icon: AlertTriangle, q: 'Which equipment has the highest downtime?' },
  { label: 'Shift comparison', icon: BarChart2, q: 'Compare breakdown patterns across shifts' },
  { label: 'Fix first?', icon: Wrench, q: 'What should we fix first to reduce downtime?' },
  { label: 'Any risks?', icon: Zap, q: 'What are the high-risk equipment and upcoming failure risks?' },
  { label: 'Give me insights', icon: Sparkles, q: 'Give me key insights and patterns from the breakdown data' },
];

// ── Build system prompt from summary ─────────────────────────────────────────
function buildSystemPrompt(summary, activeFilters) {
  if (!summary) return 'You are Breakdown Brain, an AI maintenance expert. No data is currently loaded.';

  const filterInfo = Object.entries(activeFilters || {})?.filter(([, v]) => v && v !== 'ALL')?.map(([k, v]) => `${k}: ${v}`)?.join(', ');
  const equipmentTracked = summary?.totalUniqueEquipment ?? summary?.topEquipment?.length ?? 0;

  return `You are "Breakdown Brain" — an expert industrial maintenance AI assistant embedded in a plant maintenance dashboard.

CURRENT DATASET SUMMARY (cleaned & normalized, ${summary?.total} total records):
- Total Downtime: ${summary?.totalBreakdownTime} minutes (${(summary?.totalBreakdownTime / 60)?.toFixed(1)} hours)
- MTTR: ${summary?.mttr} minutes
- Worst Equipment: ${summary?.worstEquipment}
- Top Root Cause: ${summary?.topRootCause}
- Breakdown Orders: ${summary?.breakdownCount} (${summary?.breakdownPct}% of all orders)
- Unique Equipment Tracked: ${equipmentTracked}

TOP ROOT CAUSES (standardized, no duplicates):
${summary?.topRootCauses?.slice(0, 10)?.map((r, i) => `${i + 1}. ${r?.root_cause}: ${r?.count} failures, ${r?.total_downtime} min downtime (${r?.pct}%)`)?.join('\n')}

TOP EQUIPMENT BY FAILURES:
${summary?.topEquipment?.slice(0, 10)?.map((e, i) => `${i + 1}. ${e?.equipment_id}: ${e?.count} failures, ${e?.total_downtime} min downtime`)?.join('\n')}

EQUIPMENT RISK LEVELS:
${summary?.equipmentRisk?.slice(0, 8)?.map(e => `${e?.equipment_id}: ${e?.risk_level} risk (score: ${e?.risk_score})`)?.join('\n')}

SHIFT ANALYSIS:
${Object.entries(summary?.shiftMTTR || {})?.map(([sh, mttr]) => `Shift ${sh}: MTTR=${mttr} min, Failures=${summary?.byShift?.[sh] || 0}`)?.join('\n')}

OUTLIER EVENTS (extreme downtime):
${summary?.outliers?.slice(0, 5)?.map(o => `${o?.equipment_id}: ${o?.breakdown_time_mins} min on ${o?.date} (${o?.root_cause})`)?.join('\n')}

${filterInfo ? `ACTIVE FILTERS: ${filterInfo}` : 'NO ACTIVE FILTERS — showing full dataset'}

RESPONSE RULES:
1. For ANALYTICS questions → respond with: Key Insight | Supporting Data (numbers/%) | Recommendation
2. For FAULT/ROOT CAUSE questions → respond with structured format:
   🔴 FAULT IDENTIFIED: [name]
   🔍 POSSIBLE ROOT CAUSES: [list with frequency/confidence]
   📊 SUPPORTING DATA: [seen in X cases, equipment, shift]
   ✅ RECOMMENDED ACTIONS: [step-by-step]
   ⚠️ RISK LEVEL: Low/Medium/High
3. For PREDICTION questions → predict likely failures with confidence and timeframe
4. For RECOMMENDATION questions → prioritize high-impact, specific actions
5. ALWAYS use numbers from the data above
6. NEVER use NULL, "Nil", or empty values
7. Keep responses concise but data-driven
8. Use markdown formatting (bold, bullets) for clarity
9. Reference specific equipment IDs and root cause categories from the data`;
}

// ── Message bubble component ──────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
  const isUser = msg?.role === 'user';
  const isSystem = msg?.role === 'system-info';

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-gray-400 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full">{msg?.content}</span>
      </div>
    );
  }

  return (
    <div className={`flex gap-2 mb-4 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border border-cyan-200 bg-cyan-50">
          <Brain className="w-3.5 h-3.5 text-cyan-600" />
        </div>
      )}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser
          ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-tr-sm shadow-sm'
          : 'bg-white border border-slate-200 text-gray-700 rounded-tl-sm hover:border-slate-300 transition-colors shadow-sm'
      }`}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{msg?.content}</p>
        ) : (
          <FormattedResponse content={msg?.content} />
        )}
        <p className={`text-xs mt-1.5 ${isUser ? 'text-white/60' : 'text-gray-400'}`}>
          {msg?.time}
        </p>
      </div>
    </div>
  );
};

// ── Formatted AI response renderer ───────────────────────────────────────────
const FormattedResponse = ({ content }) => {
  if (!content) return null;

  const lines = content?.split('\n');
  return (
    <div className="space-y-1">
      {lines?.map((line, i) => {
        if (!line?.trim()) return <div key={i} className="h-1" />;

        if (line?.match(/^[🔴🔍📊✅⚠️🎯💡⚡🔧]/)) {
          return (
            <p key={i} className="font-semibold text-cyan-700 text-sm mt-2 first:mt-0">
              {line}
            </p>
          );
        }
        if (line?.match(/^[-•*]\s/)) {
          return (
            <p key={i} className="text-sm text-gray-600 pl-3 flex gap-1.5">
              <span className="text-cyan-500 flex-shrink-0">•</span>
              <span>{line?.replace(/^[-•*]\s/, '')}</span>
            </p>
          );
        }
        if (line?.match(/^\d+\.\s/)) {
          return (
            <p key={i} className="text-sm text-gray-600 pl-1">
              {line}
            </p>
          );
        }
        const boldParts = line?.split(/(\*\*[^*]+\*\*)/g);
        if (boldParts?.length > 1) {
          return (
            <p key={i} className="text-sm text-gray-600">
              {boldParts?.map((part, j) =>
                part?.startsWith('**') && part?.endsWith('**')
                  ? <strong key={j} className="text-gray-800">{part?.slice(2, -2)}</strong>
                  : part
              )}
            </p>
          );
        }
        return <p key={i} className="text-sm text-gray-600">{line}</p>;
      })}
    </div>
  );
};

// ── Loading indicator ─────────────────────────────────────────────────────────
const TypingIndicator = () => (
  <div className="flex gap-2 mb-4">
    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 border border-cyan-200 bg-cyan-50">
      <Brain className="w-3.5 h-3.5 text-cyan-600" />
    </div>
    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 text-cyan-500 animate-spin" />
        <span className="text-xs text-gray-500 font-medium">Analyzing data…</span>
      </div>
    </div>
  </div>
);

// ── Main BreakdownBrain Component ─────────────────────────────────────────────
const BreakdownBrain = ({ summary, activeFilters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      setTimeout(() => inputRef?.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      const equipmentTracked = summary?.totalUniqueEquipment ?? summary?.topEquipment?.length ?? 0;
      const dataStatus = summary
        ? `I have access to **${summary?.total?.toLocaleString()} work orders** with **${summary?.topRootCauses?.length} root cause categories** and **${equipmentTracked} equipment tracked**.`
        : 'No data is currently loaded. Please upload work order data first.';

      setMessages([{
        role: 'assistant',
        content: `👋 Hi! I'm **Breakdown Brain** — your AI maintenance expert.\n\n${dataStatus}\n\nAsk me anything about breakdowns, root causes, equipment risks, or what to fix first. I'll give you data-backed answers.`,
        time: new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    }
  }, [isOpen, summary, messages?.length]);

  const sendMessage = useCallback(async (userText) => {
    if (!userText?.trim() || isLoading) return;

    const userMsg = {
      role: 'user',
      content: userText?.trim(),
      time: new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setShowSuggestions(false);

    const systemPrompt = buildSystemPrompt(summary, activeFilters);
    const apiMessages = [
      { role: 'system', content: systemPrompt },
      ...messages?.filter(m => m?.role === 'user' || m?.role === 'assistant')?.slice(-8)?.map(m => ({ role: m?.role, content: m?.content })),
      { role: 'user', content: userText?.trim() },
    ];

    try {
      const res = await getChatCompletion(
        'OPEN_AI',
        'gpt-4o-mini',
        apiMessages,
        { max_tokens: 1200 }
      );

      const content = res?.choices?.[0]?.message?.content || res?.content || 'I could not generate a response. Please try again.';

      setMessages(prev => [...prev, {
        role: 'assistant',
        content,
        time: new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ I encountered an error analyzing the data. Please try again.',
        time: new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, messages, summary, activeFilters]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    sendMessage(input);
  };

  const handleSuggestion = (q) => {
    sendMessage(q);
  };

  const handleClear = () => {
    setMessages([]);
    setShowSuggestions(true);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* ── Floating Button ── */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 flex items-center gap-2.5 px-5 py-3.5 rounded-2xl transition-all duration-300 ${
          isOpen ? 'opacity-0 pointer-events-none scale-90' : 'opacity-100 scale-100'
        } border border-cyan-300 bg-white shadow-lg hover:shadow-xl hover:border-cyan-400`}
        title="Open Breakdown Brain AI Assistant"
      >
        <div className="relative">
          <Brain className="w-5 h-5 text-cyan-600" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
        </div>
        <span className="font-semibold text-sm text-gray-800">Breakdown Brain</span>
        <Sparkles className="w-4 h-4 text-cyan-500" />
      </button>
      {/* ── Side Panel Overlay ── */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="relative w-full max-w-md h-full flex flex-col animate-slide-in-right border-l border-slate-200 bg-slate-50 shadow-2xl">

            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between flex-shrink-0 border-b border-slate-200 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl border border-cyan-200 bg-cyan-50 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-gray-800 font-bold text-base">Breakdown Brain</h2>
                    <Sparkles className="w-3.5 h-3.5 text-cyan-500" />
                  </div>
                  <p className="text-gray-400 text-xs">
                    {summary ? `${summary?.total?.toLocaleString()} records loaded` : 'No data loaded'}
                    {Object.values(activeFilters || {})?.some(v => v && v !== 'ALL') && (
                      <span className="ml-1 text-amber-500">· Filters active</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleClear}
                  className="p-1.5 rounded-lg border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50 transition-all text-gray-400 hover:text-cyan-600"
                  title="Clear conversation"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg border border-slate-200 hover:border-red-200 hover:bg-red-50 transition-all text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Active Filters Banner */}
            {Object.values(activeFilters || {})?.some(v => v && v !== 'ALL') && (
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-amber-600 font-medium">Active filters:</span>
                {Object.entries(activeFilters || {})?.filter(([, v]) => v && v !== 'ALL')?.map(([k, v]) => (
                    <span key={k} className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                      {k}: {v}
                    </span>
                  ))}
              </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              {messages?.map((msg, i) => (
                <MessageBubble key={i} msg={msg} />
              ))}

              {isLoading && <TypingIndicator />}

              {/* Suggested Questions */}
              {showSuggestions && messages?.length <= 1 && !isLoading && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 font-medium mb-2 px-1 uppercase tracking-wider">Quick questions:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SUGGESTED_QUESTIONS?.map((sq, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestion(sq?.q)}
                        className="flex items-center gap-2 p-2.5 bg-white border border-slate-200 rounded-xl text-left hover:border-cyan-300 hover:bg-cyan-50 transition-all group shadow-sm"
                      >
                        <sq.icon className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 group-hover:text-cyan-600" />
                        <span className="text-xs text-gray-500 font-medium leading-tight group-hover:text-gray-700">{sq?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Show suggestions again after conversation */}
              {!showSuggestions && messages?.length > 1 && !isLoading && (
                <div className="flex justify-center mt-2">
                  <button
                    onClick={() => setShowSuggestions(true)}
                    className="text-xs text-cyan-500 hover:text-cyan-700 flex items-center gap-1 py-1 transition-colors"
                  >
                    <MessageSquare className="w-3 h-3" />
                    Show quick questions
                  </button>
                </div>
              )}

              {showSuggestions && messages?.length > 1 && !isLoading && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 font-medium mb-2 px-1 uppercase tracking-wider">Ask more:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SUGGESTED_QUESTIONS?.slice(0, 4)?.map((sq, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestion(sq?.q)}
                        className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl text-left hover:border-cyan-300 hover:bg-cyan-50 transition-all group shadow-sm"
                      >
                        <sq.icon className="w-3 h-3 text-cyan-400 flex-shrink-0 group-hover:text-cyan-600" />
                        <span className="text-xs text-gray-500 font-medium leading-tight group-hover:text-gray-700">{sq?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-slate-200 bg-white px-4 py-3 flex-shrink-0">
              <form onSubmit={handleSubmit} className="flex gap-2 items-end">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e?.target?.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about breakdowns, root causes, risks…"
                    disabled={isLoading}
                    rows={1}
                    className="w-full resize-none border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 disabled:opacity-50 bg-slate-50 max-h-28 overflow-y-auto transition-all"
                    style={{ minHeight: '42px' }}
                    onInput={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e?.target?.scrollHeight, 112) + 'px';
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !input?.trim()}
                  className="w-10 h-10 rounded-xl border border-cyan-300 bg-cyan-50 text-cyan-600 flex items-center justify-center hover:bg-cyan-100 hover:border-cyan-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 shadow-sm"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </form>
              <p className="text-xs text-gray-400 mt-1.5 text-center">
                Powered by AI · Uses same cleaned dataset as charts
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BreakdownBrain;
